import { executeWorkflow } from '../services/mindstudio';
import type { MindStudioStatus } from '../types';
import useAuthStore from '../store/authStore';

let mindStudioStatus: MindStudioStatus = {
  initialized: false,
  scriptLoaded: false,
  iframeExists: false,
  lastError: null,
  lastAction: '',
  timestamp: Date.now()
};

export const getMindStudioStatus = () => ({ ...mindStudioStatus });

export const updateMindStudioStatus = (update: Partial<MindStudioStatus>) => {
  mindStudioStatus = {
    ...mindStudioStatus,
    ...update,
    timestamp: Date.now()
  };
};

export const resetMindStudioStatus = () => {
  mindStudioStatus = {
    initialized: false,
    scriptLoaded: false,
    iframeExists: false,
    lastError: null,
    lastAction: 'reset',
    timestamp: Date.now()
  };
};

export const cleanupMindStudio = async () => {
  try {
    updateMindStudioStatus({ lastAction: 'cleanup_started' });

    document.querySelectorAll('script[id^="mindstudio-"]').forEach(script => {
      script.remove();
    });

    document.querySelectorAll('[id^="mindstudio-"]').forEach(el => el.remove());

    if ('MindStudioSettings' in window) {
      delete (window as any).MindStudioSettings;
      updateMindStudioStatus({ initialized: false });
    }

    window.removeEventListener('message', (event: MessageEvent) => {
      if (event.data?.type?.startsWith('mindstudio:')) {
        // Handle MindStudio messages
      }
    });

    const storageTypes: ('localStorage' | 'sessionStorage')[] = ['localStorage', 'sessionStorage'];
    storageTypes.forEach(storageType => {
      const storage = window[storageType];
      if (storage) {
        Object.keys(storage).forEach(key => {
          if (key.toLowerCase().includes('mindstudio')) {
            storage.removeItem(key);
          }
        });
      }
    });

    document.querySelectorAll('[class*="mindstudio"]').forEach(el => el.remove());
    document.querySelectorAll('[id*="mindstudio"]').forEach(el => el.remove());

    document.querySelectorAll('style').forEach(style => {
      if (style.textContent?.includes('mindstudio')) {
        style.remove();
      }
    });

    await new Promise(resolve => setTimeout(resolve, 500));
    
    updateMindStudioStatus({ 
      lastAction: 'cleanup_completed',
      initialized: false,
      scriptLoaded: false,
      iframeExists: false,
      lastError: null
    });

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    updateMindStudioStatus({ 
      lastError: errorMessage,
      lastAction: 'cleanup_failed'
    });
    console.error('Error cleaning up MindStudio:', error);
    throw error;
  }
};

export const trackUsage = async (userId: string, appId: string, variables: Record<string, any>, sessionId: string): Promise<void> => {
  try {
    const response = await executeWorkflow(
      appId,
      variables,
      {
        userId,
        sessionId,
        tags: ['usage-tracking']
      }
    );

    if (response.success && response.billingCost) {
      const totalCost = response.billingCost * 1.4; // 40% markup
      useAuthStore.getState().deductCredits(totalCost);
    }
  } catch (error) {
    console.error('Failed to track usage:', error);
    throw error;
  }
};